/**
 * Knowledge Graph Safe Index (lib/knowledge-graph/safe-index.ts)
 *
 * Non-blocking, fire-and-forget wrapper around indexEntity for wiring
 * index-on-write at real entity create sites (Space tasks, Nexus
 * contacts/leads/companies, etc). Modelled on lib/nexus/audit-writer.ts:
 * it never throws into the caller's write path and logs on failure.
 *
 * Primary responsibilities:
 * - Never throw/reject into the user-facing write path (log + swallow)
 * - Skip indexing when the workspace id is unusable
 * - Delegate idempotent upsert to indexEntity
 *
 * Dependencies:
 * - lib/knowledge-graph (indexEntity)
 * - lib/logger
 */

import type { SupabaseClient } from "@supabase/supabase-js"

import { indexEntity, type EntityRecord, type IndexableKind } from "@/lib/knowledge-graph"
import { logger } from "@/lib/logger"
import type { Database } from "@/supabase/types"

// --------------

function isUsableWorkspaceId(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "" && value !== "undefined"
}

// --------------

/**
 * Index an entity into the Knowledge Graph without ever blocking or throwing
 * into the caller. Resolves to true when the node was upserted, false when the
 * write was skipped or failed. Callers may await it or not; it never rejects.
 *
 * The underlying indexEntity does an idempotent upsert keyed by
 * (workspace_id, node_type, source_id), so re-running on the same entity is
 * safe and will not duplicate rows.
 */
export async function safeIndexEntity(
  kind: IndexableKind,
  record: EntityRecord,
  workspaceId: string | null | undefined,
  supabaseClient?: SupabaseClient<Database>
): Promise<boolean> {
  if (!isUsableWorkspaceId(workspaceId)) {
    return false
  }

  try {
    await indexEntity(kind, record, workspaceId, supabaseClient)
    return true
  } catch (error) {
    logger.error("Failed to index entity into knowledge graph", {
      kind,
      workspaceId,
      error: error instanceof Error ? error.message : "Unknown error"
    })
    return false
  }
}
